import type { ChangeEvent} from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { WSRequest } from "whole-shared";
import { displayNameMaxLength, displayNameMinLength, displayNameRegExp } from "whole-shared";

import { Player } from "../db";
import { ws } from "../ws";
import { RequestButton } from "./Components";
import { Context } from "./context";
import { useTimeout } from "./hooks";

export const Settings = () => {
  const { player, updateContext } = useContext(Context);
  const [displayName, setDisplayName] = useState(player.displayName);
  const [displayNameLoading, setDisplayNameLoading] = useState(false);
  const [displayNameResult, setDisplayNameResult] = useState({ ok: player.displayName !== displayName && player.displayName !== "", status: "" });
  const [displayNameSaving, setDisplayNameSaving] = useState(false);
  const navigate = useNavigate();
  const timeout = useTimeout();

  useEffect(() => (player.isLogged() ? undefined : navigate("/")));

  const handleDisplayName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const { length } = value;
      const setDisplayNameTooShort = () => setDisplayNameResult({ ok: false, status: `${displayNameMinLength - length} missing character(s)` });

      setDisplayName(value);
      setDisplayNameLoading(false);
      timeout.clear();

      if(value === "") return setDisplayNameTooShort();
      if(! value.match(displayNameRegExp)) return setDisplayNameResult({ ok: false, status: "invalid character(s)" });
      if(length < displayNameMinLength) return setDisplayNameTooShort();
      if(length > displayNameMaxLength)
        return setDisplayNameResult({ ok: false, status: `${length - displayNameMaxLength} exceeding character(s)` });
      if(player.displayName !== "" && player.displayName === value) return setDisplayNameResult({ ok: false, status: "" });

      setDisplayNameLoading(true);
      timeout.set(() => {
        ws.request({ checkDisplayName: value }, ({ available }) => {
          setDisplayNameResult(available ? { ok: true, status: "available" } : { ok: false, status: "unavailable" });
          setDisplayNameLoading(false);
        });
      }, 1000);
    },
    [player.displayName, timeout]
  );

  const displayNameSave = useCallback(() => setDisplayNameSaving(true), []);

  const displayNameSaved = useCallback(
    ({ available, player }: WSRequest) => {
      setDisplayNameSaving(false);
      if(available === false) setDisplayNameResult({ ok: false, status: "unavailable" });
      if(player) {
        updateContext({ player: new Player(player) });
        setDisplayNameResult({ ok: false, status: "" });
      }
    },
    [updateContext]
  );

  return <div className="box panel" style={{ marginLeft: 100, marginTop: 50, padding: 30 }}>
    <h4>Settings</h4>
    <div style={{ alignItems: "baseline", display: "flex", height: 60 }}>
      <span style={{ padding: 8 }}>Display name:</span>
      <div style={{ display: "inline-block" }}>
        <input disabled={displayNameSaving} onChange={handleDisplayName} type="text" value={displayName} />
        <br />
        {displayNameLoading ? <img src="/loading.gif" width="17" /> : <span style={{ color: displayNameResult.ok ? "#00c000" : "#c00000" }}>{displayNameResult.status}</span>}
      </div>
      <RequestButton
        disabled={! displayNameResult.ok}
        onRequest={displayNameSave}
        onResponse={displayNameSaved}
        request={{ setDisplayName: displayName }}
        style={{ marginLeft: 15, width: 80 }}
        text="Save"
      />
    </div>
  </div>;
};
